import { normalize } from 'normalizr'
import { Commit } from 'vuex'

import Booking from '@/models/booking'
import Passenger from '@/models/passenger'

import axios from '@/axios-module'
import errorHandler from '@/error-handler'
import notification, { notifications } from '@/notification'
import { passengerSchema } from '@/schemas/passenger'
import { BookingsMutationTypes } from '@/store/modules/bookings'

export enum PassengersActionTypes {
  Get = '[Passengers] Get',
  Update = '[Passengers] Update',
  Remove = '[Passengers] Remove',
  ResendInvitation = '[Passengers] ResendInvitation',
}

export enum PassengersMutationTypes {
  GetSuccess = '[Passengers] GetSuccess',
  Update = '[Passengers] Update',
  UpdateRollback = '[Passengers] UpdateRollback',
  ResendInvitationSuccess = '[Passengers] ResendInvitationSuccess',
  ResendInvitationError = '[Passengers] ResendInvitationError',
  RemoveSuccess = '[Passengers] RemoveSuccess',
}

export interface State {
  passengerEntities: {
    [id: number]: Passenger
  }
  passengerIds: number[]
}

const initialState: State = {
  passengerEntities: {},
  passengerIds: [],
}

export default {
  state: initialState,
  getters: {
    getPassengerById(state: State): (id: number) => Passenger | null {
      return (id: number) => state.passengerEntities[id]
    },
    getPassengersByIds(state: State): (ids: number[]) => Passenger[] {
      return (ids: number[]) =>
        (ids || []).map((id) => state.passengerEntities[id])
    },
  },
  actions: {
    async [PassengersActionTypes.Get](
      { commit }: { commit: Commit },
      { passengerId, bookingId }: { passengerId: number; bookingId: number },
    ) {
      const response = await axios.get(
        `/customer/bookings/${bookingId}/passengers/${passengerId}`,
      )
      commit(
        PassengersMutationTypes.GetSuccess,
        normalize(response.data, passengerSchema),
      )
    },
    async [PassengersActionTypes.Update](
      { commit, state }: { commit: Commit; state: State },
      passenger: Passenger,
    ) {
      const oldPassenger = state.passengerEntities[passenger.id]
      commit(PassengersMutationTypes.Update, passenger)
      try {
        await axios.put(
          `/customer/bookings/${passenger.bookingId}/passengers/${passenger.id}`,
          { passenger },
        )
        if (passenger.canBeChanged) {
          axios.put(`/customer/passengers/${passenger.id}/invitations`)
        }
        notification('Passenger updated!', 'success')
      } catch ({
        response: {
          data: { errors },
        },
      }) {
        commit(PassengersMutationTypes.UpdateRollback, oldPassenger)
        if (errors) {
          notifications(errorHandler(errors), 'error')
        } else {
          notification('Passenger was not updated!', 'error')
        }
      }
    },
    async [PassengersActionTypes.Remove](
      { commit }: { commit: Commit },
      { passengerId, bookingId }: { passengerId: number; bookingId: number },
    ) {
      try {
        await axios.delete(
          `/customer/bookings/${bookingId}/passengers/${passengerId}`,
        )
        commit(PassengersMutationTypes.RemoveSuccess)
        notification('Passenger deleted!', 'success')
      } catch {
        notification('Error when deleting a passenger', 'error')
      }
    },
    async [PassengersActionTypes.ResendInvitation](
      { commit }: { commit: Commit },
      passengerId: number,
    ) {
      await axios.put(`/customer/passengers/${passengerId}/invitations`)
      commit(PassengersMutationTypes.ResendInvitationSuccess)
    },
  },
  mutations: {
    [PassengersMutationTypes.GetSuccess](
      state: State,
      normalizedPassenger: any,
    ) {
      state.passengerEntities = Object.assign(
        {},
        state.passengerEntities,
        normalizedPassenger.entities.passengers,
      )
    },
    [PassengersMutationTypes.Update](state: State, passenger: Passenger) {
      state.passengerEntities = {
        ...state.passengerEntities,
        [passenger.id]: passenger,
      }
    },
    [PassengersMutationTypes.UpdateRollback](
      state: State,
      oldPassenger: Passenger,
    ) {
      state.passengerEntities = {
        ...state.passengerEntities,
        [oldPassenger.id]: oldPassenger,
      }
    },
    [BookingsMutationTypes.GetSuccess](state: State, booking: Booking) {
      const passengerEntities: { [id: number]: Passenger } = {}
      const passengerIds = [...state.passengerIds]

      booking.passengers.forEach((passenger: Passenger) => {
        let { depositOutstandingCents } = passenger

        depositOutstandingCents =
          depositOutstandingCents < 0 ? 0 : depositOutstandingCents

        passengerEntities[passenger.id] = {
          ...passenger,
          bookingId: booking.id,
          depositOutstandingCents,
        }

        passengerIds.push(passenger.id)
      })

      state.passengerEntities = {
        ...state.passengerEntities,
        ...passengerEntities,
      }

      state.passengerIds = passengerIds
    },
    [PassengersMutationTypes.ResendInvitationSuccess]() {
      return undefined
    },
    [PassengersMutationTypes.ResendInvitationSuccess]() {
      return undefined
    },
    [PassengersMutationTypes.RemoveSuccess]() {
      return undefined
    },
  },
}
